import { DragEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Heading, Icon, Text } from '@sendible/design-system';
import { useTranslation } from 'react-i18next';
import { Container, Content, DashedCircle, FileTypeRow, IconsRow, SupportedFilesRow } from './index.styles';
import { useComposeBoxContext } from '../../composeBoxContext';
import { composeMediaInteractions } from '../../composeMediaInteractions';
import { COMPOSE_TABS } from '../../consts';

export const DragAndDrop = () => {
  const [showComponent, setShowComponent] = useState(false);
  const { uploadFromDevice, isMediaAttacherOpen, activeComposeBoxTab } = useComposeBoxContext();
  // A dropped file has no row to land on in the bulk importer (rows pick their media via the
  // attach dropdown), so the overlay is disabled there - but dropping a file onto a page with
  // no handler makes the browser navigate to it, losing the importer's unsaved rows, so
  // that default is blocked instead.
  const isBulkImport = activeComposeBoxTab === COMPOSE_TABS.BULK_IMPORT;
  const { t } = useTranslation('common_terms');

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const { files } = event.dataTransfer;

    setShowComponent(false);
    uploadFromDevice(Array.from(files));
  };

  const preventDragDefault = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (isMediaAttacherOpen) {
      composeMediaInteractions.setIsMediaAttacherOpen(false);
    }
  };

  useEffect(() => {
    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      if (e.dataTransfer?.types.includes('Files')) {
        setShowComponent(true);
      }
    };

    const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
      if (!e.clientX && !e.clientY) {
        setShowComponent(false);
      }
    };

    document.addEventListener('dragover', (e: unknown) => handleDragOver(e as DragEvent<HTMLDivElement>));
    document.addEventListener('dragleave', (e: unknown) => handleDragLeave(e as DragEvent<HTMLDivElement>));

    return () => {
      document.removeEventListener('dragover', (e: unknown) => handleDragOver(e as DragEvent<HTMLDivElement>));
      document.removeEventListener('dragleave', (e: unknown) => handleDragLeave(e as DragEvent<HTMLDivElement>));
    };
  }, []);

  useEffect(() => {
    if (!isBulkImport) return undefined;

    // Only file drags, and not onto a native file input (which handles its own drop): any
    // other drag - e.g. selected text into one of the importer's inputs - must keep working.
    const preventFileNavigation = (event: Event) => {
      const { dataTransfer, target } = event as unknown as globalThis.DragEvent;

      if (!dataTransfer?.types.includes('Files')) return;
      if (target instanceof Element && target.closest('input[type="file"]')) return;

      event.preventDefault();
    };

    document.addEventListener('dragover', preventFileNavigation);
    document.addEventListener('drop', preventFileNavigation);

    return () => {
      document.removeEventListener('dragover', preventFileNavigation);
      document.removeEventListener('drop', preventFileNavigation);
    };
  }, [isBulkImport]);

  return (
    <>
      {showComponent &&
        !isBulkImport &&
        createPortal(
          <Container
            onDrop={(event) => handleDrop(event)}
            onDragOver={(event) => preventDragDefault(event)}
            onDragEnter={(event) => preventDragDefault(event)}
          >
            <DashedCircle>
              <Content>
                <IconsRow>
                  <Icon
                    name="image"
                    color="brandLight"
                    strokeWidth={1.5}
                  />
                  <Icon
                    name="file"
                    color="brandLight"
                    strokeWidth={1.5}
                  />
                  <Icon
                    name="video"
                    color="brandLight"
                    strokeWidth={1.5}
                  />
                </IconsRow>
                <Heading
                  semantic="h2"
                  variation="layout-title_32"
                  color="brandLight"
                >
                  {t('drop_files')}
                </Heading>
                <SupportedFilesRow>
                  <Heading
                    semantic="h6"
                    variation="list-title_12"
                    color="brandLight"
                  >
                    {t('supported_files')}
                  </Heading>
                  <FileTypeRow>
                    <Text>{t('images')}</Text>
                    <Text>PNG, JPG, GIF, WEBP, BMP</Text>
                  </FileTypeRow>
                  <FileTypeRow>
                    <Text>{t('videos')}</Text>
                    <Text>MP4, MOV</Text>
                  </FileTypeRow>
                  <FileTypeRow>
                    <Text>{t('documents')}</Text>
                    <Text>{t('pdf_linkedin')}</Text>
                  </FileTypeRow>
                </SupportedFilesRow>
              </Content>
            </DashedCircle>
          </Container>,
          document.getElementById('modal-root') as HTMLElement
        )}
    </>
  );
};
